import { geminiEnterpriseAgentsService } from '../gemini-enterprise/geminiEnterpriseAgentsService';
import { customAgentsIntentService } from '../intent-analysis/customAgentsIntentService';
import { customAgentsConversationAdapter, customAgentsUsersAdapter } from './customAgentsAdapters';
import { customAgentsAccessAdapter, customAgentsSourceAdapter } from './customAgentsSecurityAdapters';

/**
 * Raised when a shared (Gemini-built) page asks the Custom Agents service for
 * data the Custom backend has no contract for.
 */
export class CustomAgentsUnsupportedError extends Error {
  constructor(method) {
    super(`Custom Agents does not support ${method} yet`);
    this.name = 'CustomAgentsUnsupportedError';
    this.method = method;
  }
}

export const isCustomAgentsUnsupported = (error) => error instanceof CustomAgentsUnsupportedError;

/**
 * Build the Custom Agents service boundary for the shared pages
 * (CUSTOM_AGENTS_GEMINI_TRANSITION_REPORT.md §12.2).
 *
 * Shared pages call whatever the Gemini Enterprise service exposes, and
 * AgentServiceContext falls back to that very service when nothing is
 * provided — so a shared page mounted for Custom Agents would quietly show
 * Gemini data. Here every method of that contract exists, but only the ones a
 * Custom adapter implements return data; the rest reject with
 * CustomAgentsUnsupportedError, which a page shows as an error rather than a
 * convincing zero.
 */
export function createCustomAgentsService(implemented) {
  const service = {};
  Object.entries(geminiEnterpriseAgentsService).forEach(([method, fn]) => {
    if (typeof fn === 'function') {
      service[method] = () => Promise.reject(new CustomAgentsUnsupportedError(method));
    }
  });
  return Object.freeze(Object.assign(service, implemented));
}

export const customAgentsService = createCustomAgentsService({
  getIntentAnalysis: customAgentsIntentService.getIntentAnalysis,
  getSessionFilters: customAgentsIntentService.getSessionFilters,
  getSessions: customAgentsConversationAdapter.getSessions,
  getSessionDetails: customAgentsConversationAdapter.getSessionDetails,
  getThreats: customAgentsConversationAdapter.getThreats,
  getConversationDriftAnalysis: customAgentsConversationAdapter.getConversationDriftAnalysis,
  getUsers: customAgentsUsersAdapter.getUsers,
  getSources: customAgentsSourceAdapter.getSources,
  getPostureMetrics: customAgentsAccessAdapter.getPostureMetrics,
  getPostureFindings: customAgentsAccessAdapter.getPostureFindings,
});

export default customAgentsService;
